import { useState, useMemo } from 'react';
import { DataTable, EmptyState, Pagination, type ColumnDef } from '@vnetwork/ui/data';
import { Alert } from '@vnetwork/ui/feedback';
import { Button } from '@vnetwork/ui/primitives';
import { formatDateTime, formatMoney } from '@/lib/format';
import { useAffiliateConversions, type AffiliateConversion } from '@/api/affiliate';
import { paginationView } from '@/pages/customers/pagination-summary';
import { EntityLink } from '@/components/entity-link';

const PAGE_SIZE = 20;

interface ConversionsTabProps {
  affiliateId: number;
}

/** Shared column definitions for the conversions table — used by the manager tab and the employee self-view. */
export function buildConversionColumns(): ColumnDef<AffiliateConversion, unknown>[] {
  return [
    {
      id: 'click_id',
      header: 'Click ID',
      cell: ({ row }) => row.original.click_id,
    },
    {
      id: 'customer_id',
      header: 'Customer',
      cell: ({ row }) => (
        <EntityLink
          to={`/customers/${row.original.customer_id}`}
          id={row.original.customer_id}
          name={row.original.customer_name}
        />
      ),
    },
    {
      id: 'customer_email',
      header: 'Email',
      cell: ({ row }) => row.original.customer_email || '—',
    },
    {
      id: 'total_revenue',
      header: () => <div style={{ textAlign: 'right' }}>Revenue</div>,
      cell: ({ row }) => (
        <div style={{ textAlign: 'right' }}>{formatMoney(row.original.total_revenue ?? 0)}</div>
      ),
    },
    {
      id: 'total_commission',
      header: () => <div style={{ textAlign: 'right' }}>Commission</div>,
      cell: ({ row }) => (
        <div style={{ textAlign: 'right', fontWeight: 'var(--vn-font-weight-semibold)' }}>
          {formatMoney(row.original.total_commission ?? 0)}
        </div>
      ),
    },
    {
      id: 'commission_status',
      header: 'Commission Status',
      cell: ({ row }) => row.original.commission_status || '—',
    },
    {
      id: 'converted_at',
      header: 'Converted At',
      cell: ({ row }) => formatDateTime(row.original.converted_at),
    },
    {
      id: 'customer_created_at',
      header: 'Customer Created',
      cell: ({ row }) => formatDateTime(row.original.customer_created_at),
    },
  ];
}

/**
 * Referral signups (Conversions) tab — server-paginated conversion list.
 * Columns: click_id, customer (EntityLink id–name), email, revenue, commission,
 * commission_status, converted_at, customer_created_at.
 */
export function ConversionsTab({ affiliateId }: ConversionsTabProps) {
  const [page, setPage] = useState(1);
  const { data, isLoading, isError, refetch } = useAffiliateConversions(affiliateId, {
    limit: PAGE_SIZE,
    offset: (page - 1) * PAGE_SIZE,
  });

  const rows = data?.results ?? [];
  const total = data?.pagination?.total ?? rows.length;
  const pageCount = data?.pagination?.total_pages ?? 1;

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const columns = useMemo(() => buildConversionColumns(), []);

  if (isError) {
    return (
      <Alert
        tone="danger"
        title="Failed to load conversions"
        actions={
          <Button size="sm" variant="ghost" onClick={() => refetch()}>
            Retry
          </Button>
        }
      />
    );
  }

  const view = paginationView(page, PAGE_SIZE, rows.length, total, pageCount);

  return (
    <DataTable
      columns={columns}
      data={rows}
      enableSorting={false}
      getRowId={(r) => String(r.click_id)}
      emptyState={
        <EmptyState
          title="No referral signups"
          description="Customers who signed up via this affiliate's link will appear here."
        />
      }
      footer={
        isLoading ? null : (
          <Pagination
            page={page}
            pageCount={view.pageCount}
            onPageChange={setPage}
            summary={view.summary}
          />
        )
      }
    />
  );
}
